import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Tab, Tabs, LinearProgress,
  FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, TextField, Button
} from '@mui/material';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, LineChart, Line, PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import {
  VaccinesOutlined, PeopleOutlined, CheckCircleOutlined,
  WarningAmberOutlined, FilterAltOutlined, ClearOutlined
} from '@mui/icons-material';
import StatCard from '../components/StatCard';
import EswatiniMap from '../components/EswatiniMap';
import { getImmunEnrollments, getImmunAttendance, getImmunMissed,
  getImmunHotspot, getImmunFilters } from '../utils/api';

const TT  = { backgroundColor:'#111827', border:'1px solid rgba(0,191,165,0.2)', borderRadius:8, fontSize:12 };
const REGION_COORDS = {
  'Hhohho':    { lat:-26.32, lng:31.13 },
  'Manzini':   { lat:-26.50, lng:31.37 },
  'Lubombo':   { lat:-26.40, lng:31.80 },
  'Shiselweni':{ lat:-27.00, lng:31.20 },
  'Matsapha':  { lat:-26.49, lng:31.31 },
};
const REASON_COLORS = {
  'Vaccine Stock Out':                '#FFB300',
  'Baby was Sick':                    '#FF5252',
  'No bus fare/ Transport':           '#7C4DFF',
  'Told to come back at another time':'#00BFA5',
  'Baby catching up on previous vaccine':'#40C4FF',
  'Baby not reach age/appointment date':'#69F0AE',
  'Appointment in the next 1-2 weeks':'#FF6D00',
  'Unavailable to attend':            '#E040FB',
  'Other':                            '#90A4AE',
};
const PIE_COLORS = ['#00BFA5','#FFB300','#40C4FF','#FF5252','#CE93D8','#69F0AE','#FF7043','#7C4DFF','#90A4AE'];
const STAGE_ORDER = ['At Birth','Week 6','Week 10','Week 14','Month 6','Month 9',
  'Month 12','Month 18','Month 24','Month 30','Month 36','Month 42','Month 48','Month 54','Month 60'];

const cleanStage = name => name
  .replace('Flow_ImmunizationRecord_','').replace(/_V[12]$/,'')
  .replace('At_Birth','At Birth').replace(/Week(\d)/,'Week $1')
  .replace(/Month(\d)/,'Month $1').trim();

export default function ImmunizationPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading,   setLoading]   = useState(true);
  const [filters,   setFilters]   = useState({ region:'', facility:'', stage:'', dateFrom:'', dateTo:'' });
  const [filterOpts,setFilterOpts]= useState({ regions:[], facilities:[], stages:[] });

  const [enrollData,  setEnrollData]  = useState(null);
  const [attendData,  setAttendData]  = useState(null);
  const [missedData,  setMissedData]  = useState(null);
  const [hotspotData, setHotspotData] = useState(null);

  useEffect(() => {
    getImmunFilters().then(r => setFilterOpts(r.data)).catch(console.error);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const f = { ...filters };
      Object.keys(f).forEach(k => !f[k] && delete f[k]);
      const [e, a, m, h] = await Promise.all([
        getImmunEnrollments(f), getImmunAttendance(f),
        getImmunMissed(f),      getImmunHotspot(f)
      ]);
      setEnrollData(e.data); setAttendData(a.data);
      setMissedData(m.data); setHotspotData(h.data);
    } catch(err) { console.error(err); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({ region:'', facility:'', stage:'', dateFrom:'', dateTo:'' });

  // ── Filter Bar ───────────────────────────────────────────────
  const FilterBar = () => (
    <Card sx={{ mb:2 }}>
      <CardContent sx={{ py:1.5, '&:last-child':{pb:1.5} }}>
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
          <FilterAltOutlined sx={{ color:'primary.main', fontSize:20 }}/>
          <FormControl size="small" sx={{ minWidth:140 }}>
            <InputLabel>Region</InputLabel>
            <Select value={filters.region} label="Region" onChange={e=>setFilter('region',e.target.value)}>
              <MenuItem value="">All Regions</MenuItem>
              {filterOpts.regions.map(r => <MenuItem key={r.region} value={r.region}>{r.region}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth:160 }}>
            <InputLabel>Facility</InputLabel>
            <Select value={filters.facility} label="Facility" onChange={e=>setFilter('facility',e.target.value)}>
              <MenuItem value="">All Facilities</MenuItem>
              {filterOpts.facilities
                .filter(f => !filters.region || f.region===filters.region)
                .map(f => <MenuItem key={f.facility} value={f.facility}>{f.facility}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth:140 }}>
            <InputLabel>Stage</InputLabel>
            <Select value={filters.stage} label="Stage" onChange={e=>setFilter('stage',e.target.value)}>
              <MenuItem value="">All Stages</MenuItem>
              {filterOpts.stages.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" type="date" label="From" value={filters.dateFrom}
            onChange={e=>setFilter('dateFrom',e.target.value)} InputLabelProps={{shrink:true}} sx={{width:145}}/>
          <TextField size="small" type="date" label="To" value={filters.dateTo}
            onChange={e=>setFilter('dateTo',e.target.value)} InputLabelProps={{shrink:true}} sx={{width:145}}/>
          {Object.values(filters).some(Boolean) && (
            <Button size="small" startIcon={<ClearOutlined/>} onClick={clearFilters} color="warning">
              Clear Filters
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  // ── Tab 1: Enrollments ───────────────────────────────────────
  const EnrollmentsTab = () => {
    const d = enrollData;
    if (!d) return null;
    return (
      <Box>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={4}>
            <StatCard title="Enrolled Mothers" value={parseInt(d.total||0).toLocaleString()}
              icon={<PeopleOutlined/>} color="#00BFA5" loading={loading}/>
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard title="Regions Covered" value={d.byRegion.filter(r=>['Hhohho','Manzini','Lubombo','Shiselweni'].includes(r.region)).length}
              icon={<VaccinesOutlined/>} color="#FFB300" loading={loading}/>
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard title="Facilities Covered" value={d.byFacility.filter(f=>f.facility!=='Unknown').length}
              icon={<CheckCircleOutlined/>} color="#CE93D8" loading={loading}/>
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={2}>Enrollment Over Time</Typography>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={d.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
                    <XAxis dataKey="month" tick={{fontSize:10,fill:'#90A4AE'}} angle={-45} textAnchor="end" height={50}/>
                    <YAxis tick={{fontSize:11,fill:'#90A4AE'}}/>
                    <Tooltip contentStyle={TT}/>
                    <Line type="monotone" dataKey="enrolled" name="Enrolled Mothers"
                      stroke="#00BFA5" strokeWidth={2} dot={{r:3}} activeDot={{r:5}}/>
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{height:'100%'}}>
              <CardContent>
                <Typography variant="h6" mb={2}>Language Preference</Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={d.byLanguage} dataKey="count" nameKey="language"
                      cx="50%" cy="50%" outerRadius={80} label={({language,percent})=>
                        `${language} ${(percent*100).toFixed(1)}%`}>
                      {d.byLanguage.map((_,i) => <Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
                    </Pie>
                    <Tooltip contentStyle={TT}/>
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={2}>Enrollment by Region</Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={d.byRegion} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
                    <XAxis type="number" tick={{fontSize:11,fill:'#90A4AE'}}/>
                    <YAxis dataKey="region" type="category" tick={{fontSize:11,fill:'#90A4AE'}} width={90}/>
                    <Tooltip contentStyle={TT}/>
                    <Bar dataKey="enrolled" name="Enrolled" fill="#00BFA5" radius={[0,4,4,0]}
                      label={{position:'right',fontSize:11,fill:'#90A4AE'}}/>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={1}>Top Facilities</Typography>
                <TableContainer sx={{maxHeight:220}}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Facility</TableCell>
                        <TableCell>Region</TableCell>
                        <TableCell align="right">Enrolled</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {d.byFacility.slice(0,10).map((f,i) => (
                        <TableRow key={i} hover>
                          <TableCell><Typography variant="caption">{f.facility}</Typography></TableCell>
                          <TableCell><Typography variant="caption" color="text.secondary">{f.region}</Typography></TableCell>
                          <TableCell align="right">
                            <Typography variant="caption" fontWeight={700} color="primary.main">{f.enrolled}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // ── Tab 2: Attendance ────────────────────────────────────────
  const AttendanceTab = () => {
    const d = attendData;
    if (!d) return null;
    const totalAttended = d.byStage.reduce((s,r)=>s+r.attended,0);
    const totalMissed   = d.byStage.reduce((s,r)=>s+r.missed,0);
    const totalRecords  = d.byStage.reduce((s,r)=>s+r.total,0);
    return (
      <Box>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={6} sm={3}>
            <StatCard title="Total Records" value={totalRecords.toLocaleString()} icon={<VaccinesOutlined/>} color="#00BFA5" loading={loading}/>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard title="Attended" value={totalAttended.toLocaleString()} icon={<CheckCircleOutlined/>} color="#69F0AE" loading={loading}/>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard title="Missed" value={totalMissed.toLocaleString()} icon={<WarningAmberOutlined/>} color="#FF5252" loading={loading}/>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard title="Attendance Rate"
              value={totalRecords>0 ? `${((totalAttended/totalRecords)*100).toFixed(1)}%` : '0%'}
              icon={<CheckCircleOutlined/>} color="#FFB300" loading={loading}/>
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={2}>Attendance by Immunization Stage</Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={d.byStage} margin={{bottom:60}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
                    <XAxis dataKey="stage" tick={{fontSize:10,fill:'#90A4AE'}} angle={-45} textAnchor="end" height={70}/>
                    <YAxis tick={{fontSize:11,fill:'#90A4AE'}}/>
                    <Tooltip contentStyle={TT}/>
                    <Legend/>
                    <Bar dataKey="attended" name="Attended" fill="#00BFA5" radius={[3,3,0,0]} stackId="a"/>
                    <Bar dataKey="missed"   name="Missed"   fill="#FF5252" radius={[3,3,0,0]} stackId="a"/>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{height:'100%'}}>
              <CardContent>
                <Typography variant="h6" mb={1}>Stage Attendance Rate</Typography>
                <Box sx={{overflowY:'auto', maxHeight:320}}>
                  {d.byStage.filter(s=>s.total>0).map((s,i) => (
                    <Box key={i} mb={1.5}>
                      <Box display="flex" justifyContent="space-between" mb={0.3}>
                        <Typography variant="caption" fontWeight={600}>{s.stage}</Typography>
                        <Typography variant="caption" color={s.rate>70?'#69F0AE':s.rate>40?'#FFB300':'#FF5252'}
                          fontFamily='"IBM Plex Mono",monospace'>
                          {s.rate}%
                        </Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={Math.min(s.rate,100)}
                        sx={{height:6, borderRadius:3, bgcolor:'rgba(255,255,255,0.06)',
                          '& .MuiLinearProgress-bar':{bgcolor: s.rate>70?'#00BFA5':s.rate>40?'#FFB300':'#FF5252', borderRadius:3}}}/>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>Monthly Attendance Trend</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={d.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
                <XAxis dataKey="month" tick={{fontSize:10,fill:'#90A4AE'}} angle={-45} textAnchor="end" height={50}/>
                <YAxis tick={{fontSize:11,fill:'#90A4AE'}}/>
                <Tooltip contentStyle={TT}/>
                <Legend/>
                <Line type="monotone" dataKey="attended" name="Attended" stroke="#00BFA5" strokeWidth={2} dot={{r:3}}/>
                <Line type="monotone" dataKey="missed"   name="Missed"   stroke="#FF5252" strokeWidth={2} dot={{r:3}}/>
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // ── Tab 3: Missed Vaccines ───────────────────────────────────
  const MissedTab = () => {
    const d = missedData;
    if (!d) return null;
    const totalMissed   = d.byRegion.reduce((s,r)=>s+parseInt(r.total_missed||0),0);
    const totalStockOut = d.byRegion.reduce((s,r)=>s+parseInt(r.stock_out||0),0);

    // Top reasons overall
    const reasonTotals = {};
    d.byReason.forEach(r => {
      if (!r.reason) return;
      reasonTotals[r.reason] = (reasonTotals[r.reason]||0) + parseInt(r.count||0);
    });
    const topReasons = Object.entries(reasonTotals)
      .sort((a,b)=>b[1]-a[1])
      .map(([reason,count]) => ({ reason, count }));

    // Region chart data
    const regionData = d.byRegion.map(r => ({
      region: r.region,
      'Vaccine Stock Out': parseInt(r.stock_out||0),
      'Baby was Sick':     parseInt(r.baby_sick||0),
      'Transport':         parseInt(r.transport||0),
      'Told Come Back':    parseInt(r.told_come_back||0),
      'Catching Up':       parseInt(r.catching_up||0),
      'Other':             parseInt(r.other||0),
    }));

    return (
      <Box>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={6} sm={3}>
            <StatCard title="Total Missed" value={totalMissed.toLocaleString()}
              icon={<WarningAmberOutlined/>} color="#FF5252" loading={loading}/>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard title="Vaccine Stock Out" value={totalStockOut.toLocaleString()}
              icon={<WarningAmberOutlined/>} color="#FFB300" loading={loading}/>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard title="Regions Affected"
              value={d.byRegion.filter(r=>parseInt(r.total_missed||0)>0).length}
              icon={<WarningAmberOutlined/>} color="#FF7043" loading={loading}/>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard title="Stock Out Rate"
              value={totalMissed>0 ? `${((totalStockOut/totalMissed)*100).toFixed(1)}%` : '0%'}
              icon={<WarningAmberOutlined/>} color="#CE93D8" loading={loading}/>
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={2}>Vaccine Stock Out by Region</Typography>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={regionData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
                    <XAxis type="number" tick={{fontSize:10,fill:'#90A4AE'}}/>
                    <YAxis dataKey="region" type="category" tick={{fontSize:11,fill:'#90A4AE'}} width={90}/>
                    <Tooltip contentStyle={TT}/>
                    <Legend/>
                    <Bar dataKey="Vaccine Stock Out" fill="#FFB300" stackId="r" radius={[0,3,3,0]}/>
                    <Bar dataKey="Baby was Sick"     fill="#FF5252" stackId="r"/>
                    <Bar dataKey="Transport"         fill="#7C4DFF" stackId="r"/>
                    <Bar dataKey="Told Come Back"    fill="#00BFA5" stackId="r"/>
                    <Bar dataKey="Other"             fill="#90A4AE" stackId="r"/>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={7}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={1}>Missed Visits by Region</Typography>
                <TableContainer sx={{maxHeight:280}}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Region</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Stock Out</TableCell>
                        <TableCell align="right">Sick</TableCell>
                        <TableCell align="right">Transport</TableCell>
                        <TableCell align="right">Other</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {d.byRegion.map((r,i) => (
                        <TableRow key={i} hover>
                          <TableCell><Typography variant="caption" fontWeight={600}>{r.region}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="caption" fontWeight={700} color="#FF5252">{r.total_missed}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="caption" color="#FFB300">{r.stock_out||0}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="caption" color="#FF5252">{r.baby_sick||0}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="caption" color="#7C4DFF">{r.transport||0}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="caption" color="text.secondary">{r.other||0}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>Reasons for Missing Vaccine Visits by Region</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={regionData} margin={{bottom:20}}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
                <XAxis dataKey="region" tick={{fontSize:11,fill:'#90A4AE'}}/>
                <YAxis tick={{fontSize:11,fill:'#90A4AE'}}/>
                <Tooltip contentStyle={TT}/>
                <Legend/>
                <Bar dataKey="Vaccine Stock Out" fill="#FFB300" stackId="s"/>
                <Bar dataKey="Baby was Sick"     fill="#FF5252" stackId="s"/>
                <Bar dataKey="Transport"         fill="#7C4DFF" stackId="s"/>
                <Bar dataKey="Told Come Back"    fill="#00BFA5" stackId="s"/>
                <Bar dataKey="Catching Up"       fill="#40C4FF" stackId="s"/>
                <Bar dataKey="Other"             fill="#90A4AE" stackId="s" radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // ── Tab 4: Hotspot ───────────────────────────────────────────
  const HotspotTab = () => {
    const d = hotspotData;
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [selectedReason, setSelectedReason] = useState(null);
    if (!d) return null;

    const allReasons = [...new Set(d.missedByRegion.map(r => r.reason).filter(Boolean))];
    const totalMissed = d.topMissed.reduce((s,r) => s + parseInt(r.count||0), 0);

    // Filter data for selected region
    const filteredData = selectedRegion
      ? d.missedByRegion.filter(r => r.region === selectedRegion)
      : d.missedByRegion;

    const filteredTopMissed = selectedReason
      ? d.topMissed.filter(r => r.reason === selectedReason)
      : d.topMissed;

    return (
      <Box>
        <Grid container spacing={2}>
          {/* Map */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={1}>
                  Missed Vaccines by Region
                  {selectedReason && <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                    — filtered: {selectedReason}
                  </Typography>}
                </Typography>
                <EswatiniMap
                  data={d.missedByRegion}
                  selectedRegion={selectedRegion}
                  onRegionClick={setSelectedRegion}
                  selectedReason={selectedReason}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Most frequent reasons */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height:'100%' }}>
              <CardContent>
                <Typography variant="h6" mb={2}>
                  Most Frequent Reasons
                  {selectedRegion && <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                    — {selectedRegion}
                  </Typography>}
                </Typography>
                {d.topMissed.filter(r=>r.reason).map((r,i) => {
                  const regionCount = selectedRegion
                    ? (d.missedByRegion.find(row => row.region === selectedRegion && row.reason === r.reason)?.count || 0)
                    : r.count;
                  const regionPct = selectedRegion
                    ? (filteredData.reduce((s,row) => s+parseInt(row.count||0),0) > 0
                      ? ((parseInt(regionCount)/filteredData.reduce((s,row)=>s+parseInt(row.count||0),0))*100).toFixed(1)
                      : 0)
                    : r.pct;
                  const isActive = selectedReason === r.reason;
                  return (
                    <Box key={i} mb={1.5} onClick={() => setSelectedReason(isActive ? null : r.reason)}
                      sx={{ cursor:'pointer', p:0.5, borderRadius:1,
                        bgcolor: isActive ? 'rgba(255,255,255,0.05)' : 'transparent',
                        '&:hover':{ bgcolor:'rgba(255,255,255,0.03)' } }}>
                      <Box display="flex" justifyContent="space-between" mb={0.3}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Box sx={{ width:8, height:8, borderRadius:'50%',
                            bgcolor: REASON_COLORS[r.reason]||'#90A4AE', flexShrink:0 }}/>
                          <Typography variant="caption" fontWeight={isActive?700:600}
                            sx={{ maxWidth:'70%', color: isActive ? '#fff' : 'text.primary' }} noWrap>
                            {r.reason}
                          </Typography>
                        </Box>
                        <Typography variant="caption" fontFamily='"IBM Plex Mono",monospace'
                          color={REASON_COLORS[r.reason]||'#90A4AE'}>
                          {regionCount} ({regionPct}%)
                        </Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={Math.min(parseFloat(regionPct||0),100)}
                        sx={{ height:7, borderRadius:4, bgcolor:'rgba(255,255,255,0.06)',
                          '& .MuiLinearProgress-bar':{
                            bgcolor: REASON_COLORS[r.reason]||'#90A4AE', borderRadius:4 }}}/>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={2} display="flex" alignItems="center" gap={1.5}>
        <VaccinesOutlined sx={{color:'#CE93D8', fontSize:32}}/>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{color:'#CE93D8'}}>
            Immunization Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            TextWell4Health · Make SMS Platform · Kingdom of Eswatini
          </Typography>
        </Box>
      </Box>

      {/* Use Case Tabs */}
      <Tabs value={activeTab} onChange={(_,v)=>setActiveTab(v)} sx={{mb:2,
        bgcolor:'rgba(255,255,255,0.03)', borderRadius:1, p:0.5,
        '& .MuiTab-root':{fontFamily:'"IBM Plex Mono",monospace', fontSize:'0.75rem', minHeight:40,
          color:'text.secondary', borderRadius:1},
        '& .Mui-selected':{color:'#CE93D8 !important', bgcolor:'rgba(206,147,216,0.1)'},
        '& .MuiTabs-indicator':{display:'none'}}}>
        <Tab label="📋 Enrollments" value={0}/>
        <Tab label="💉 Attendance of Vaccine Visits" value={1}/>
        <Tab label="⚠️ Missed Vaccines" value={2}/>
        <Tab label="🗺️ Vaccines Hotspot" value={3}/>
      </Tabs>

      <FilterBar/>
      {loading && <LinearProgress sx={{mb:1,'& .MuiLinearProgress-bar':{bgcolor:'#CE93D8'}}}/>}

      {activeTab === 0 && <EnrollmentsTab/>}
      {activeTab === 1 && <AttendanceTab/>}
      {activeTab === 2 && <MissedTab/>}
      {activeTab === 3 && <HotspotTab/>}
    </Box>
  );
}
